import React, {useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import Sidebar from "../../components/Sidebar";
import DashboardDoctorHeader from "../../components/MainHeader/DashboardDoctorHeader";

import icon1 from "../../assets/images/setting-icon.svg";
import tooltipIcon from "../../assets/images/tooltip-icon.svg";
import morePlusCircleOutlineIcon from "../../assets/images/more-plus-circle-outline-icon.svg";
import {
  deleteOptionApi,
  deleteQuestionApi,
  fetchQuestionApi,
  fetchSettingApi,
  updateQuestionApi,
  updateSettingApi,
  visitQuestionApi,
} from "../../services/apiService";
import {toast} from "react-toastify";
import LoadingPage from "../../components/LoadingPage";
import {useUserContext} from "../../context/UserContext";
import {updateUserInLocalStorage} from "../../utils/userLocalStorage";
import {queryKeys} from "../../queries/queryKeys";

import cancelIcon from "../../assets/images/cancel-btn.svg";

const WorkspaceConfig = () => {
  const queryClient = useQueryClient();
  const {setUserData} = useUserContext();
  const [settingDetails, setSettingDetails] = useState();
  const [screenLoading, setScreenLoading] = useState(false);
  const [futureInputValue, setFutureInputValue] = useState("");
  const [questionsList, setQuestionsList] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedItemForDelete, setSelectedItemForDelete] = useState();
  const [deleteBtnDis, setDeleteBtnDis] = useState(false);

  const handleFutureInputChange = event => {
    setFutureInputValue(event.target.value);
  };

  const handleFutureBlur = () => {
    if (futureInputValue && futureInputValue !== settingDetails?.future_days) {
      handleSettingChange("future_days", futureInputValue);
    }
  };

  const getSettings = async () => {
    setScreenLoading(true);
    try {
      let response = await fetchSettingApi();
      if (response) {
        if (response.data.res === true) {
          setSettingDetails(response.data.data);
          setFutureInputValue(response.data.data.future_days);
          
          // Update user data in localStorage with settings
          updateUserInLocalStorage({
            "users.emergency_slot": response.data.data.emergency_slot,
            "users.consultation_type": response.data.data.consultation_type,
            "users.future_days": response.data.data.future_days
          }, setUserData);
        } else {
          toast.error(response.data.msg, {autoClose: 1500});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    } finally {
      setScreenLoading(false);
    }
  };

  const handleSettingChange = async (column, value) => {
    setScreenLoading(true);
    try {
      const fd = new FormData();
      fd.append("column", column);
      fd.append("value", value);
      let response = await updateSettingApi(fd);
      if (response) {
        if (response.data.res === true) {
          toast.success(response.data.msg, {autoClose: 1500});
          
          // Update specific setting in localStorage immediately
          const updates = {};
          updates[`users.${column}`] = column === "emergency_slot" || column === "consultation_type" ? parseInt(value) : value;
          updateUserInLocalStorage(updates, setUserData);
          
          await getSettings();
        } else {
          toast.error(response.data.msg, {autoClose: 1500});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    } finally {
      setScreenLoading(false);
    }
  };

  const getQuestions = async () => {
    setScreenLoading(true);
    try {
      let response = await fetchQuestionApi();
      if (response) {
        if (response.data.res === true) {
          setQuestionsList(response.data.data);
        } else {
          toast.error(response.data.msg, {autoClose: 1500});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    } finally {
      setScreenLoading(false);
    }
  };

  const visitQuestionChange = async value => {
    setScreenLoading(true);
    try {
      const fd = new FormData();
      fd.append("id", questionsList[0]?.id);
      fd.append("value", value);
      let response = await visitQuestionApi(fd);
      if (response) {
        if (response.data.res === true) {
          toast.success(response.data.msg, {autoClose: 1500});
          getQuestions();
        } else {
          toast.error(response.data.msg, {autoClose: 1500});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    } finally {
      setScreenLoading(false);
    }
  };

  const handleAddQuestion = () => {
    let tempArray = [...questionsList];
    tempArray.push({ask: 0, shows: 0, question: "", label: "Q" + (tempArray.length + 1), delete: 0, options: []});
    setQuestionsList(tempArray);
  };

  const handleAddOptions = questIndex => {
    let tempArray = [...questionsList];
    let selectedQues = tempArray[questIndex];
    selectedQues.options.push({question_id: selectedQues?.id, options: "", priority: 0, deleteOption: 0});
    setQuestionsList(tempArray);
  };

  const handleQuestionValueChange = (questIndex, value) => {
    let tempArray = [...questionsList];
    let selectedQues = tempArray[questIndex];
    selectedQues.question = value;
    setQuestionsList(tempArray);
  };

  const handleAskShowCheckChange = (type, questIndex, value) => {
    let tempArray = [...questionsList];
    let selectedQues = tempArray[questIndex];
    if (type === "ask") {
      selectedQues.ask = value;
    } else if (type === "shows") {
      selectedQues.shows = value;
    }
    console.log("selectedQues", selectedQues);
    setQuestionsList(tempArray);
  };

  const handleOptionValueChange = (questIndex, optionIndex, value) => {
    let tempArray = [...questionsList];
    let selectedQuesOptions = tempArray[questIndex].options[optionIndex];
    selectedQuesOptions.options = value;
    setQuestionsList(tempArray);
  };

  const handlePriorityChange = (questIndex, optionIndex, value) => {
    let tempArray = [...questionsList];
    tempArray[questIndex].options.forEach(option => {
      option.priority = 0;
    });
    let selectedQuesOptions = tempArray[questIndex].options[optionIndex];
    selectedQuesOptions.priority = value;
    setQuestionsList(tempArray);
  };

  const handleDeleteQuestion = (quesItem, quesIndex) => {
    quesItem.typeForDelete = "question";
    quesItem.quesIndex = quesIndex;
    if (quesItem.id) {
      quesItem.fromServer = true;
      setSelectedItemForDelete(quesItem);
      setShowDeletePopup(true);
    } else {
      quesItem.fromServer = false;
      setSelectedItemForDelete(quesItem);
      setShowDeletePopup(true);
    }
  };

  const handleDeleteOption = (optItem, optIndex, quesItem, quesIndex) => {
    optItem.typeForDelete = "option";
    optItem.optIndex = optIndex;
    optItem.quesIndex = quesIndex;
    if (optItem.id) {
      optItem.fromServer = true;
      setSelectedItemForDelete(optItem);
      setShowDeletePopup(true);
    } else {
      optItem.fromServer = false;
      setSelectedItemForDelete(optItem);
      setShowDeletePopup(true);
    }
  };

  const handleConfirmDeleteQuestion = async () => {
    if (selectedItemForDelete) {
      let tempArray = [...questionsList];
      if (selectedItemForDelete?.typeForDelete === "question") {
        if (selectedItemForDelete.fromServer) {
          setDeleteBtnDis(true);
          try {
            const fd = new FormData();
            fd.append("id", selectedItemForDelete?.id);
            let response = await deleteQuestionApi(fd);
            if (response) {
              if (response.data.res === true) {
                getQuestions();
                toast.success(response.data.msg, {autoClose: 1500});
                setShowDeletePopup(false);
              } else {
                toast.error(response.data.msg, {autoClose: 1500});
              }
            }
          } catch (error) {
            console.error(error);
            toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
          } finally {
            setDeleteBtnDis(false);
          }
        } else {
          tempArray.splice(selectedItemForDelete?.quesIndex);
          setQuestionsList(tempArray);
          setShowDeletePopup(false);
          toast.success("Successfully deleted.", {autoClose: 1500});
        }
      } else if (selectedItemForDelete?.typeForDelete === "option") {
        if (selectedItemForDelete.fromServer) {
          setDeleteBtnDis(true);
          try {
            const fd = new FormData();
            fd.append("id", selectedItemForDelete?.id);
            let response = await deleteOptionApi(fd);
            if (response) {
              if (response.data.res === true) {
                getQuestions();
                toast.success(response.data.msg, {autoClose: 1500});
                setShowDeletePopup(false);
              } else {
                toast.error(response.data.msg, {autoClose: 1500});
              }
            }
          } catch (error) {
            console.error(error);
            toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
          } finally {
            setDeleteBtnDis(false);
          }
        } else {
          tempArray[selectedItemForDelete.quesIndex]?.options?.splice(selectedItemForDelete.optIndex, 1);
          setQuestionsList(tempArray);
          setShowDeletePopup(false);
          toast.success("Successfully deleted.", {autoClose: 1500});
        }
      }
    }
  };

  const handleSaveChanges = async () => {
    let isQuestBlank = 0;
    let isNotOption = 0;
    let isOptionBlank = 0;

    questionsList.forEach(element => {
      if (!element.question) {
        isQuestBlank = 1;
      } else if (element.question && element.options?.length === 0) {
        isNotOption = 1;
      } else {
        element.options?.forEach(chElement => {
          if (!chElement.options) {
            isOptionBlank = 1;
          }
        });
      }
    });
    if (isQuestBlank === 1) {
      toast.warning("Questiion can not be blank.", {autoClose: 1500});
      return;
    }
    if (isNotOption === 1) {
      toast.warning("Each question must have at least one option.", {autoClose: 1500});
      return;
    }
    if (isOptionBlank === 1) {
      toast.warning("Options cannot be blank. Please ensure all options have values.", {autoClose: 1500});
      return;
    }
    setScreenLoading(true);
    try {
      const fd = new FormData();
      questionsList.map((item, index) => {
        fd.append(`questions[${index}][id]`, item?.id ? item?.id : "");
        fd.append(`questions[${index}][question]`, item?.question ? item?.question : "");
        fd.append(`questions[${index}][ask]`, item?.ask ? item?.ask : "0");
        fd.append(`questions[${index}][shows]`, item?.shows ? item?.shows : "0");
        fd.append(`questions[${index}][disabled]`, item?.disabled ? item?.disabled : "0");
        fd.append(`questions[${index}][label]`, item?.label ? item?.label : "Q" + (index + 1));
        // fd.append(`questions[${index}][no]`, item?.no ? item?.no : "0");

        // if (item?.options?.length > 0) {
        //   fd.append(`questions[${index}][options]`, "1");
        // } else {
        //   fd.append(`questions[${index}][options]`, "0");
        // }

        item?.options?.map((chItem, chIndex) => {
          fd.append(`questions[${index}][options][${chIndex}][id]`, chItem?.id ? chItem?.id : "");
          fd.append(`questions[${index}][options][${chIndex}][options]`, chItem?.options);
          fd.append(`questions[${index}][options][${chIndex}][priority]`, chItem?.priority ? chItem?.priority : "0");
          fd.append(`questions[${index}][options][${chIndex}][question_id]`, chItem?.question_id ? chItem?.question_id : "");
        });
      });

      let response = await updateQuestionApi(fd);
      if (response) {
        if (response.data.res === true) {
          toast.success(response.data.msg, {autoClose: 1500});
          await queryClient.invalidateQueries({
            queryKey: queryKeys.configs.getQuersions(),
          });
          getQuestions();
        } else {
          toast.error(response.data.msg, {autoClose: 1500});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", {autoClose: 1500});
    } finally {
      setScreenLoading(false);
    }
  };

  useEffect(() => {
    getSettings();
    getQuestions();
  }, []);

  return (
    <div className="doctormain">
      <Sidebar />
      <div className="rightSec">
        <DashboardDoctorHeader />
        <div className="mainDiv">
          {screenLoading && <LoadingPage />}
          <div className="hdnDiv nurseTop">
            <h2>
              <span>
                <img src={icon1} alt="Configuration icon" />
                Workspace Configuration
              </span>
            </h2>
          </div>
          <div className="general-config-sec">
            <h3>General Config</h3>
            <ul className="config-list">
              <li className="first-list">
                <label>Allow people with priority tag to make reservations in my emergency slots</label>
                <div className="listSwitch">
                  <input
                    type="checkbox"
                    id="switch"
                    checked={settingDetails?.emergency_slot === 1 ? true : false}
                    onChange={e => handleSettingChange("emergency_slot", e.target.checked ? "1" : "0")}
                  />
                  <label htmlFor="switch">Toggle</label>
                </div>
              </li>
              <li>
                <label>Type Of Consultation?</label>
                <ul className="radio-list">
                  <li onClick={() => handleSettingChange("consultation_type", "1")}>
                    <span className="label-span">Video</span>
                    <div className="StylesRadio">
                      <input
                        type="radio"
                        name="videoRadioBtn"
                        checked={settingDetails?.consultation_type === 1 ? true : false}
                        onChange={e => handleSettingChange("consultation_type", "1")}
                      />
                      <span className="checkmark"></span>
                    </div>
                  </li>
                  <li onClick={() => handleSettingChange("consultation_type", "2")}>
                    <span className="label-span">Cabinet</span>
                    <div className="StylesRadio">
                      <input
                        type="radio"
                        name="videoRadioBtn"
                        checked={settingDetails?.consultation_type === 2 ? true : false}
                        onChange={e => handleSettingChange("consultation_type", "2")}
                      />
                      <span className="checkmark"></span>
                    </div>
                  </li>
                  <li onClick={() => handleSettingChange("consultation_type", "0")}>
                    <span className="label-span">Both</span>
                    <div className="StylesRadio">
                      <input
                        type="radio"
                        name="videoRadioBtn"
                        checked={settingDetails?.consultation_type === 0 ? true : false}
                        onChange={e => handleSettingChange("consultation_type", "0")}
                      />
                      <span className="checkmark"></span>
                    </div>
                  </li>
                </ul>
              </li>
              <li>
                <label>Can make a reservation maximum days in the future</label>
                <ul className="small-input-list">
                  <li>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type here"
                      value={futureInputValue}
                      onChange={handleFutureInputChange}
                      onBlur={handleFutureBlur}
                    />
                    <span className="days-span">Day’s</span>
                  </li>
                </ul>
              </li>
            </ul>
            <h3>My reservation questions config</h3>
            <div className="config-row">
              <div className="left-config">
                <ul className="left-config-list">
                  <li>
                    <ul className="left-sub-config-list">
                      <li>
                        <span className="tooltip-span">Ask</span>{" "}
                        <div className="tooltip-sec">
                          <img src={tooltipIcon} alt="Tooltip icon" />
                          <div className="tooltip-content">Activate / Deactivate</div>
                        </div>
                      </li>
                      <li>
                        <span className="tooltip-span">Show</span>{" "}
                        <div className="tooltip-sec">
                          <img src={tooltipIcon} alt="Tooltip icon" />
                          <div className="tooltip-content">Show the answer in the doc calendar</div>
                        </div>
                      </li>
                    </ul>
                  </li>
                  {questionsList?.length > 0 ? (
                    questionsList?.map((item, index) => (
                      <li key={index}>
                        <ul className="left-sub-config-list2">
                          <li>
                          <div className="StylesCheckbox" onClick={() => handleAskShowCheckChange("ask", index, item?.ask === 1 ? 0 : 1)}>
                            <input
                                type="checkbox"
                                checked={item?.ask}
                                onChange={e => handleAskShowCheckChange("ask", index, e.target.checked ? 1 : 0)}
                              />
                              <span className="checkmark"></span>
                            </div>
                          </li>
                          <li>
                            <div className="StylesCheckbox" onClick={() => handleAskShowCheckChange("shows", index, item?.shows === 1 ? 0 : 1)}>
                              <input
                                type="checkbox"
                                checked={item?.shows}
                                onChange={e => handleAskShowCheckChange("shows", index, e.target.checked ? 1 : 0)}
                              />
                              <span className="checkmark"></span>
                            </div>
                          </li>
                          <li>
                            <span className="num-span">Q{index + 1}:</span>
                          </li>
                          <li>
                            <div className="input-box">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Question"
                                value={item?.question}
                                readOnly={index === 0}
                                onChange={e => handleQuestionValueChange(index, e.target.value)}
                              />
                              {index > 0  ? (//&& item?.delete !== 1
                                <button className="trash-btn" onClick={() => handleDeleteQuestion(item, index)}></button>
                              ) : null}
                              {index === questionsList?.length - 1 ? (
                                <img
                                  src={morePlusCircleOutlineIcon}
                                  alt="More plus circle outline icon"
                                  className="more-plus-circle-outline-icon"
                                  onClick={() => handleAddQuestion()}
                                />
                              ) : null}
                            </div>
                            {index > 0 ? (
                              <ul className="left-sub-config-list3">
                                <li>
                                  <div className="addOptionSection" onClick={() => handleAddOptions(index)}>
                                    <img src={morePlusCircleOutlineIcon} alt="More plus circle outline icon" className="more-plus-circle-outline-icon" />
                                    <span className="addoptionsSpan">Add Option</span>
                                  </div>
                                </li>
                                {item?.options?.map((chItem, chIndex) => (
                                  <>
                                    <li key={index + chIndex + 1}>
                                      <div className="input-box2">
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Enter here"
                                          value={chItem?.options}
                                          onChange={e => handleOptionValueChange(index, chIndex, e.target.value)}
                                        />
                                        <button
                                          className={chItem?.priority === 1 ? "btn" : "btn disabled-btn"}
                                          onClick={() => handlePriorityChange(index, chIndex, chItem?.priority === 1 ? 0 : 1)}>
                                          Q.priority
                                        </button>
                                      </div>
                                      {/* <img src={morePlusCircleOutlineIcon} alt="More plus circle outline icon" className="more-plus-circle-outline-icon" /> */}
                                      {/* {chItem?.deleteOption !== 1 ? ( */}
                                        <button className="trash-btn" onClick={() => handleDeleteOption(chItem, chIndex, item, index)}></button>
                                      {/* ) : null} */}
                                    </li>
                                  </>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        </ul>
                      </li>
                    ))
                  ) : (
                    <li>
                      <ul className="left-sub-config-list2">
                        <li>
                          <div className="StylesCheckbox"></div>
                        </li>
                        <li>
                          <div className="StylesCheckbox"></div>
                        </li>
                        <li>
                          <div className="StylesCheckbox"></div>
                        </li>
                        <li>
                          <input type="text" className="form-control" placeholder="" readOnly />
                        </li>
                      </ul>
                    </li>
                  )}
                  <div className="password-edit configSaveBtn">
                    <button onClick={() => handleSaveChanges()}>Save Changes</button>
                  </div>
                  {/* <li>
                    <ul className="left-sub-config-list2">
                      <li>
                        <div className="StylesCheckbox">
                          <input type="checkbox" checked />
                          <span className="checkmark"></span>
                        </div>
                      </li>
                      <li>
                        <div className="StylesCheckbox">
                          <input type="checkbox" checked />
                          <span className="checkmark"></span>
                        </div>
                      </li>
                      <li>
                        <span className="num-span">Q2:</span>
                      </li>
                      <li>
                        <div className="input-box">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Did you already visit this doctor"
                            value={questionsList[1]?.question}
                            readOnly
                          />
                          <img src={morePlusCircleOutlineIcon} alt="More plus circle outline icon" className="more-plus-circle-outline-icon" />
                        </div>
                        <ul className="left-sub-config-list3">
                          <li>
                            <div className="input-box2">
                              <input type="text" className="form-control" placeholder="Head problem pain" />
                              <button className="btn disabled-btn">Q.priority</button>
                            </div>
                            <button className="trash-btn"></button>
                          </li>
                          <li>
                            <div className="input-box2">
                              <input type="text" className="form-control" placeholder="Big pain in my head" />
                              <button className="btn disabled-btn">Q.priority</button>
                            </div>
                            <button className="trash-btn"></button>
                          </li>
                          <li>
                            <div className="input-box2">
                              <input type="text" className="form-control" placeholder="Can’t breathn" />
                              <button className="btn">Q.priority</button>
                            </div>
                            <button className="trash-btn"></button>
                          </li>
                          <li>
                            <div className="input-box2">
                              <input type="text" className="form-control" placeholder="Other" />
                              <button className="btn disabled-btn">Q.priority</button>
                            </div>
                            <button className="trash-btn"></button>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li> */}
                </ul>
              </div>
              <div className="right-config">
                <div className="right-config-center">
                  <label>If no:</label>
                  <div className="btn-sec">
                    <button className={questionsList[0]?.no === 1 ? "btn" : "btn2"} onClick={() => visitQuestionChange("1")}>
                      Show message and stop booking
                    </button>
                    <button className={questionsList[0]?.no === 0 ? "btn" : "btn2"} onClick={() => visitQuestionChange("0")}>
                      Do nothing and continue booking
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDeletePopup ? (
        <div className="modal DeleteModal">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="CloseButton" onClick={() => setShowDeletePopup(false)}></button>
            <h3>
              Are you want to delete{" "}
              {selectedItemForDelete?.typeForDelete === "question"
                ? `${selectedItemForDelete?.label}?`
                : selectedItemForDelete?.option
                ? `option - "${selectedItemForDelete?.options}"?`
                : `option`}
              ?
            </h3>
            <ul className="ButtonList">
              <li>
                <button className="ModalCloseBtn configPage" onClick={() => handleConfirmDeleteQuestion()} disabled={deleteBtnDis}>
                  {deleteBtnDis ? "Please wait..." : "Yes"}
                </button>
              </li>
              <li>
                <button className="CancelBtn" onClick={() => setShowDeletePopup(false)}>
                  No
                </button>
              </li>
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WorkspaceConfig;
